import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Optional,
  signal,
} from '@angular/core';
import { QuestionnaireInstanceService } from '../../../api/services/questionnaire-instance.service';
import { Observable } from 'rxjs';
import { QuestionnaireInstanceModel } from '../../../api/models/questionnaire-instance-model';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { QuestionnaireModel } from '../../../api/models/questionnaire-model';
import { QuestionnaireService } from '../../../api/services/questionnaire.service';
import { AuthService } from '../../../services/auth.service';
import { ToastService } from '../../../services/toast.service';
import { KeyValue } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuestionnaireInstancesInitialisePostParams } from '../../../api/fn/questionnaire-instance/questionnaire-instance-initialise-post';
import { StrictHttpResponse } from '../../../api/strict-http-response';
import { AnswerModel } from '../../../api/models/answer-model';
import { InviteLinkDispatchType } from '../../../enums/invite-link-dispatch-type';
import { QuestionType } from '../../../api/models/question-type';
import { AnswerOptionModel } from '../../../api/models/answer-option-model';
import { SelectedAnswerOptionModel } from '../../../api/models/selected-answer-option-model';
import { SurveyRespondentType } from '../../../enums/survey-respondent-type';
import { SurveyRespondentService } from '../../../api/services/survey-respondent.service';
import { PaginatedList } from '../../../api/models/paginator/paginated-list';
import { Ethnicity } from '../../../enums/ethnicity';
import { Gender } from '../../../enums/gender';
import { DialogService } from '../../../services/dialog.service';
import { RespondentAlreadyExistsModalComponent } from '../../../components/modals/respondent-already-exists-modal/respondent-already-exists-modal.component';
import { SurveyRespondentConflictType } from '../../../enums/survey-respondent-conflict-type';
import { SurveyRespondentModel } from '../../../api/models/survey-respondent-model';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  standalone: false,
})
export class DetailsComponent implements OnInit {
  detailsForm = new FormGroup({});

  searchText = signal('');
  dropdownOpen = signal(false);

  private destroyRef = inject(DestroyRef);

  // Dropdown states for custom dropdowns
  questionnaireDropdownOpen = signal(false);
  respondentTypeDropdownOpen = signal(false);
  completionDropdownOpen = signal(false);
  genderDropdownOpen = signal(false);
  ethnicityDropdownOpen = signal(false);

  // Edit mode for existing respondent
  isEditingRespondent = signal(false);
  originalRespondentValues: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    mobileNumber: string | null;
    dateOfBirth: string | null;
    ethnicities: string[];
    gender: string | null;
    customGender: string | null;
    customEthnicity: string | null;
  } | null = null;

  newRecipientDetailsForm = new FormGroup({
    firstName: new FormControl<string>('', Validators.required),
    lastName: new FormControl<string>('', Validators.required),
    email: new FormControl<string>('', Validators.required),
    mobileNumber: new FormControl<string | null>(null),
    dateOfBirth: new FormControl<string | null>(null),
    ethnicities: new FormControl<string[]>([]),
    gender: new FormControl<string | null>(null),
    customGender: new FormControl<string | null>(null),
    customEthnicity: new FormControl<string | null>(null),
  });

  existingRespondentGuid?: string = undefined;

  /**
   * The questionnaire selected
   */
  selectedQuestionnaireGuid: string | undefined;

  /**
   * Questionnaires available to be started
   */
  availableQuestionnaires: QuestionnaireModel[] = [];

  /**
   * The email address of the invitee who will receive this questionnaire
   */
  inviteeEmailAddress: string | undefined;

  /**
   * The mobile number of the invitee who will receive this questionnaire
   */
  inviteeMobileNumber: string | undefined;

  /**
   * Reference to the current details form model
   */
  currentDetailsForm: QuestionnaireModel | null | undefined;

  /**
   * The type of remote dispatch, if selected
   */
  selectedDispatchType: InviteLinkDispatchType | '' = '';

  /**
   * Automation tag guids selected for the questionnaire
   */
  selectedAutomationTagGuids: string[] = [];

  automationScheduledQuestionnaireRecipient?: string;
  automationScheduledQuestionnaireDispatchType: InviteLinkDispatchType =
    InviteLinkDispatchType.Email;

  private _surveyRespondentType: SurveyRespondentType =
    SurveyRespondentType.Anonymous;

  get surveyRespondentType(): SurveyRespondentType {
    return this._surveyRespondentType;
  }

  /** Whether the respondent panel (right side) should be visible */
  get showRespondentPanel(): boolean {
    return this._surveyRespondentType !== SurveyRespondentType.Anonymous;
  }

  setRespondentType(target: SurveyRespondentType) {
    this._surveyRespondentType = target;
  }

  existingSurveyRespondentsPaginatedList?: PaginatedList<SurveyRespondentModel>;

  sending = false;
  protected readonly Object = Object;
  protected readonly InviteLinkDispatchType = InviteLinkDispatchType;
  protected readonly QuestionType = QuestionType;
  protected readonly SurveyRespondentType = SurveyRespondentType;
  protected readonly Ethnicity = Ethnicity;
  protected readonly Gender = Gender;

  constructor(
    private questionnaireInstanceService: QuestionnaireInstanceService,
    private router: Router,
    private questionnaireService: QuestionnaireService,
    private authService: AuthService,
    private toastService: ToastService,
    private surveyRespondentService: SurveyRespondentService,
    private dialogService: DialogService,
    @Optional() private dialogRef: MatDialogRef<DetailsComponent> | null,
  ) {
    this.newRecipientDetailsForm.controls.email.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.preFillInviteRespondentField();
      });

    this.newRecipientDetailsForm.controls.mobileNumber.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.preFillInviteRespondentField();
      });
  }

  ngOnInit() {
    this.resetScrollPosition();

    // Get available questionnaires
    this.questionnaireService
      .questionnairesGet(this.authService.getAuthorisationHeaders(), {
        includeDetailsForm: true,
        includeMainSurveysOnly: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (questionnaires: QuestionnaireModel[]) => {
          this.availableQuestionnaires = questionnaires;
          this.selectedQuestionnaireGuid = questionnaires[0]?.guid;
          this.updateDetailsForm();
        },
        error: () => {
          this.toastService.error('Could Not Retrieve Questionnaires');
        },
      });

    // Get existing respondents
    this.surveyRespondentService
      .surveyRespondentsGet(this.authService.getAuthorisationHeaders(), {
        accountGuid: this.authService.getActiveAccountGuid()!,
        pageSize: 999999,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((respondents: PaginatedList<SurveyRespondentModel>) => {
        this.existingSurveyRespondentsPaginatedList = respondents;
      });
  }

  private resetScrollPosition(): void {
    if (this.isDialog) {
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  filteredRespondents = computed(() => {
    const list = this.existingSurveyRespondentsPaginatedList;
    const search = this.searchText().toLowerCase();
    if (!list) return [];

    const filtered = search
      ? list.records.filter((r) =>
          this.getFormattedRespondentName(r).toLowerCase().includes(search),
        )
      : [...list.records];

    return filtered.sort((a, b) => {
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      return aLast.localeCompare(bLast);
    });
  });

  /**
   * Update form based on selected questionnaire
   */
  updateDetailsForm() {
    if (!this.selectedQuestionnaireGuid) {
      return;
    }

    const form = this.availableQuestionnaires.filter(
      (q) => q.guid === this.selectedQuestionnaireGuid,
    )[0]?.detailsForm;

    this.currentDetailsForm = form;

    // Clear form field
    this.detailsForm = new FormGroup({});

    // If there is no form, simply return
    if (!form) {
      return;
    }

    // Otherwise, construct the form from the returned entity
    for (const formField of form.questions) {
      let initialValue: unknown = '';
      if (formField.type === QuestionType.MultipleChoice) {
        initialValue = [];
      }

      this.detailsForm.addControl(
        formField.text!,
        new FormControl(initialValue),
      );
    }
  }

  /**
   * Handle search input for existing respondent search
   */
  onSearchInput(event: Event) {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.searchText.set(value);
    this.dropdownOpen.set(true);
    this.existingRespondentGuid = undefined;
  }

  cancel() {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }

    this.router.navigate(['admin/home']);
  }

  /*
    Initialises a new questionnaire instance using the form data
   */
  initialise() {
    const surveyRespondentConflictType =
      this.validateSurveyRespondentAlreadyExists();

    if (surveyRespondentConflictType != null) {
      // If new survey respondent conflicts with existing, display modal prompting user to use
      // existing survey respondent

      let conflictingField: string | null;
      if (surveyRespondentConflictType === SurveyRespondentConflictType.email) {
        conflictingField = this.newRecipientDetailsForm.controls.email.value;
      } else if (
        surveyRespondentConflictType ===
        SurveyRespondentConflictType.mobileNumber
      ) {
        conflictingField =
          this.newRecipientDetailsForm.controls.mobileNumber.value;
      } else {
        conflictingField = `${this.newRecipientDetailsForm.controls.firstName.value} ${this.newRecipientDetailsForm.controls.lastName.value}`;
      }

      const dialogRef = this.dialogService.openDialog(
        RespondentAlreadyExistsModalComponent,
        {
          fieldValue: conflictingField,
          conflictType: surveyRespondentConflictType,
        },
      );
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result) =>
            this.handleRespondentAlreadyExistsModalClosed(
              surveyRespondentConflictType,
              result,
            ),
        });

      return;
    }

    const formAnswers: AnswerModel[] = [];

    // Set form answers based on form state
    if (this.currentDetailsForm?.questions) {
      for (const formField of this.currentDetailsForm.questions) {
        const formControlValue = this.detailsForm.get(formField.text)?.value;

        let freeText: string | undefined = undefined;
        const answerOptions: SelectedAnswerOptionModel[] = [];

        if (
          formField.type === QuestionType.FreeText ||
          formField.type === QuestionType.Date
        ) {
          freeText = formControlValue;
        } else if (formField.type === QuestionType.SingleChoice) {
          if (formControlValue) {
            answerOptions.push({
              guid: formControlValue,
              freeText: '',
            });
          }
        } else if (formField.type === QuestionType.MultipleChoice) {
          for (const answerOptionGuid of formControlValue) {
            answerOptions.push({
              guid: answerOptionGuid,
              freeText: '',
            });
          }
        }

        formAnswers.push({
          questionGuid: formField.guid,
          freeText: freeText,
          selectedAnswerOptions: answerOptions,
        });
      }
    }

    const params: QuestionnaireInstancesInitialisePostParams = {
      body: {
        questionnaireGuid: this.selectedQuestionnaireGuid,
        formAnswers: formAnswers,
        automationTagGuids: this.selectedAutomationTagGuids,
        automationScheduledQuestionnaireRecipient:
          this.automationScheduledQuestionnaireRecipient,
        automationScheduledQuestionnaireDispatchType:
          this.automationScheduledQuestionnaireDispatchType,
      },
    };

    if (this.selectedDispatchType) {
      params.body!.dispatchType = this.selectedDispatchType;
    }

    if (this.selectedDispatchType === InviteLinkDispatchType.Email) {
      if (this.surveyRespondentType === SurveyRespondentType.Anonymous) {
        params.body!.dispatchTo = this.inviteeEmailAddress;
      } else if (this.surveyRespondentType === SurveyRespondentType.Existing) {
        params.body!.dispatchTo = this.getSelectedRespondent()!.email;
      } else {
        params.body!.dispatchTo =
          this.newRecipientDetailsForm.controls.email.value!;
      }
    } else if (
      this.selectedDispatchType === InviteLinkDispatchType.TextMessage
    ) {
      if (this.surveyRespondentType === SurveyRespondentType.Anonymous) {
        params.body!.dispatchTo = this.inviteeMobileNumber;
      } else if (this.surveyRespondentType === SurveyRespondentType.Existing) {
        params.body!.dispatchTo = this.getSelectedRespondent()!.mobileNumber;
      } else {
        params.body!.dispatchTo =
          this.newRecipientDetailsForm.controls.mobileNumber.value!;
      }
    }

    const currentEthnicities =
      this.newRecipientDetailsForm.controls.ethnicities.value ?? [];
    // Avoid mutating the form control array; always construct a new payload array.
    const ethnicities =
      currentEthnicities.includes(Ethnicity.other) &&
      this.newRecipientDetailsForm.controls.customEthnicity.value
        ? [
            ...currentEthnicities,
            this.newRecipientDetailsForm.controls.customEthnicity.value,
          ]
        : [...currentEthnicities];

    // Set survey respondent if not anonymous
    if (this.surveyRespondentType === SurveyRespondentType.New) {
      params.body!.newSurveyRespondent = {
        firstName: this.newRecipientDetailsForm.controls.firstName.value!,
        lastName: this.newRecipientDetailsForm.controls.lastName.value!,
        emailAddress: this.newRecipientDetailsForm.controls.email.value!,
        mobileNumber: this.newRecipientDetailsForm.controls.mobileNumber.value,
        dateOfBirth: this.newRecipientDetailsForm.controls.dateOfBirth.value,
        ethnicities: ethnicities,
        gender:
          this.newRecipientDetailsForm.controls.gender.value ===
          Gender.selfDescribe
            ? this.newRecipientDetailsForm.controls.customGender.value
            : this.newRecipientDetailsForm.controls.gender.value,
      };
    } else if (this.surveyRespondentType === SurveyRespondentType.Existing) {
      params.body!.existingSurveyRespondentGuid = this.existingRespondentGuid;
    }

    this.sending = true;

    const results$: Observable<StrictHttpResponse<QuestionnaireInstanceModel>> =
      this.questionnaireInstanceService.initialisePost$Json$Response(
        params,
        this.authService.getAuthorisationHeaders(),
      );

    results$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.sending = false;

        if (res.status === 204) {
          // If we get a 204 response, remote invite has been dispatched, and so we should present toast
          // and go to questionnaire invites view
          this.toastService.success('Successfully Dispatched Invite Link');
          this.navigateToQuestionnaireInvitesPage();
        } else {
          if (!this.selectedDispatchType) {
            // If successful and no dispatch type specified, navigate to welcome page, passing the newly created
            // questionnaire instance guid as well as the name from the form to the view
            const instance = res.body;
            const questionnaireInstanceGuid = instance.guid;

            if (!questionnaireInstanceGuid) {
              this.toastService.error('Could Not Initialise Questionnaire');
              return;
            }

            this.questionnaireInstanceService.questionnaireInstanceGuid =
              questionnaireInstanceGuid;
            sessionStorage.setItem(
              'activeQuestionnaireInstanceGuid',
              questionnaireInstanceGuid,
            );

            const firstName =
              this.newRecipientDetailsForm.controls.firstName.value;
            const lastName =
              this.newRecipientDetailsForm.controls.lastName.value;
            const fullName =
              firstName && lastName
                ? `${firstName} ${lastName}`
                : firstName || lastName || '';

            const navigation = this.router.navigate(['welcome'], {
              queryParams: { questionnaireInstanceGuid },
              state: {
                name: fullName,
                activeQuestionnaireInstanceGuid: questionnaireInstanceGuid,
              },
              replaceUrl: true,
            });

            this.dialogRef?.close();
            void navigation;
          } else {
            // Successful with dispatch type specified. Show invites list if survey respondent is anonymous or if
            // dispatching via web link, otherwise show respondents detail view
            const instance = res.body;
            if (
              instance.surveyRespondentGuid &&
              this.selectedDispatchType !== InviteLinkDispatchType.WebLink
            ) {
              void this.router.navigate([
                'admin/respondents',
                instance.surveyRespondentGuid,
              ]);
            } else {
              const inviteLinkGuid =
                this.selectedDispatchType === InviteLinkDispatchType.WebLink
                  ? instance.inviteLinkGuid
                  : undefined;

              this.navigateToQuestionnaireInvitesPage(inviteLinkGuid);
            }
          }
        }
      },
      error: () => {
        this.sending = false;
        this.toastService.error('Could Not Initialise Questionnaire');
      },
    });
  }

  validateSurveyRespondentAlreadyExists(): SurveyRespondentConflictType | null {
    if (this.surveyRespondentType !== SurveyRespondentType.New) {
      return null;
    }

    // Return null if the existing respondents list hasn't loaded yet
    if (!this.existingSurveyRespondentsPaginatedList) {
      return null;
    }

    const emailAddress = this.newRecipientDetailsForm.controls.email.value;
    const emailAlreadyExists =
      this.existingSurveyRespondentsPaginatedList.records.some(
        (sr) => sr.email.toLowerCase() === emailAddress?.toLowerCase(),
      );

    if (emailAddress && emailAlreadyExists) {
      return SurveyRespondentConflictType.email;
    }

    const mobileNumber =
      this.newRecipientDetailsForm.controls.mobileNumber.value;
    const mobileNumberAlreadyExists =
      this.existingSurveyRespondentsPaginatedList.records.some(
        (sr) => sr.mobileNumber?.toLowerCase() === mobileNumber?.toLowerCase(),
      );

    if (mobileNumber && mobileNumberAlreadyExists) {
      return SurveyRespondentConflictType.mobileNumber;
    }

    const firstName = this.newRecipientDetailsForm.controls.firstName.value;
    const lastName = this.newRecipientDetailsForm.controls.lastName.value;
    const nameAlreadyExists =
      this.existingSurveyRespondentsPaginatedList.records.some(
        (sr) =>
          sr.firstName?.toLowerCase() === firstName?.toLowerCase() &&
          sr.lastName?.toLowerCase() === lastName?.toLowerCase(),
      );

    if (firstName && lastName && nameAlreadyExists) {
      return SurveyRespondentConflictType.name;
    }

    return null;
  }

  navigateToQuestionnaireInvitesPage(inviteLinkGuid?: string) {
    void this.router.navigate(
      ['admin/questionnaire', this.selectedQuestionnaireGuid, 'invites'],
      inviteLinkGuid ? { queryParams: { inviteLinkGuid } } : {},
    );
  }

  onInviteLinkDispatchTypeChanged() {
    this.inviteeMobileNumber = undefined;
    this.inviteeEmailAddress = undefined;
    this.preFillInviteRespondentField();
  }

  preFillInviteRespondentField() {
    if (this.selectedDispatchType === InviteLinkDispatchType.Email) {
      if (this.newRecipientDetailsForm.controls.email.value) {
        this.inviteeEmailAddress =
          this.newRecipientDetailsForm.controls.email.value;
      } else if (this.existingRespondentGuid) {
        this.inviteeEmailAddress =
          this.existingSurveyRespondentsPaginatedList?.records.filter(
            (r) => r.guid === this.existingRespondentGuid,
          )[0]?.email;
      }
    } else if (
      this.selectedDispatchType === InviteLinkDispatchType.TextMessage
    ) {
      if (this.newRecipientDetailsForm.controls.mobileNumber.value) {
        this.inviteeMobileNumber =
          this.newRecipientDetailsForm.controls.mobileNumber.value;
      } else if (this.existingRespondentGuid) {
        this.inviteeMobileNumber =
          this.existingSurveyRespondentsPaginatedList?.records.filter(
            (r) => r.guid === this.existingRespondentGuid,
          )[0]?.mobileNumber;
      }
    }
  }

  shouldDisableNextButton() {
    // Disable next button for social media and website dispatch types, as they are not yet supported
    if (
      this.selectedDispatchType === InviteLinkDispatchType.SocialMedia ||
      this.selectedDispatchType === InviteLinkDispatchType.Website
    ) {
      return true;
    }

    // Disable next button if is creating a new survey respondent, but details form is invalid
    if (
      this.surveyRespondentType === SurveyRespondentType.New &&
      !this.newRecipientDetailsForm.valid
    ) {
      return true;
    }

    if (!this.validateNewSurveyRespondentDispatch()) {
      return true;
    }

    return !this.validateExistingSurveyRespondentDispatch();
  }

  getDetailsFormFieldType(
    formFieldKey: string,
  ): QuestionType | null | undefined {
    if (!this.currentDetailsForm) {
      return null;
    }

    return this.currentDetailsForm.questions.filter(
      (q) => q.text === formFieldKey,
    )[0].type;
  }

  getDetailsFormQuestionAnswerOptions(
    formFieldKey: string,
  ): AnswerOptionModel[] {
    if (!this.currentDetailsForm) {
      return [];
    }

    return (
      this.currentDetailsForm.questions.filter(
        (q) => q.text === formFieldKey,
      )[0].answerOptions ?? []
    );
  }

  handleRespondentAlreadyExistsModalClosed(
    conflictType: SurveyRespondentConflictType,
    useExisting: boolean,
  ) {
    if (useExisting) {
      let surveyRespondent: SurveyRespondentModel | undefined;
      if (conflictType === SurveyRespondentConflictType.email) {
        surveyRespondent =
          this.existingSurveyRespondentsPaginatedList?.records.filter(
            (sr) =>
              sr.email.toLowerCase() ===
              this.newRecipientDetailsForm.controls.email.value?.toLowerCase(),
          )[0];
      } else if (conflictType === SurveyRespondentConflictType.mobileNumber) {
        surveyRespondent =
          this.existingSurveyRespondentsPaginatedList?.records.filter(
            (sr) =>
              sr.mobileNumber?.toLowerCase() ===
              this.newRecipientDetailsForm.controls.mobileNumber.value?.toLowerCase(),
          )[0];
      } else {
        const firstName = this.newRecipientDetailsForm.controls.firstName.value;
        const lastName = this.newRecipientDetailsForm.controls.lastName.value;
        surveyRespondent =
          this.existingSurveyRespondentsPaginatedList?.records.filter(
            (sr) =>
              sr.firstName?.toLowerCase() === firstName?.toLowerCase() &&
              sr.lastName?.toLowerCase() === lastName?.toLowerCase(),
          )[0];
      }

      if (!surveyRespondent) {
        return; // Safety check - should not happen
      }

      this.setRespondentType(SurveyRespondentType.Existing);
      this.existingRespondentGuid = surveyRespondent.guid;
      this.newRecipientDetailsForm.reset();
      this.initialise();
    }
  }

  getFormattedRespondentName(surveyRespondent: SurveyRespondentModel) {
    if (surveyRespondent.firstName && surveyRespondent.lastName) {
      return `${surveyRespondent.lastName}, ${surveyRespondent.firstName}`;
    } else if (surveyRespondent.firstName) {
      return surveyRespondent.firstName;
    } else if (surveyRespondent.lastName) {
      return surveyRespondent.lastName;
    }

    return '';
  }

  getSelectedRespondent() {
    if (!this.existingRespondentGuid) {
      return null;
    }

    return this.existingSurveyRespondentsPaginatedList?.records.filter(
      (sr) => sr.guid === this.existingRespondentGuid,
    )[0];
  }

  /**
   * Prefill the form with existing respondent data
   */
  prefillFormWithExistingRespondent() {
    const respondent = this.getSelectedRespondent();
    if (!respondent) {
      return;
    }

    // Format dateOfBirth to YYYY-MM-DD for HTML date input
    // Use local date parts to avoid timezone shift (toISOString() converts to UTC)
    let formattedDateOfBirth: string | null = null;
    if (respondent.dateOfBirth) {
      const date = new Date(respondent.dateOfBirth);
      if (!isNaN(date.getTime())) {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        formattedDateOfBirth = `${yyyy}-${mm}-${dd}`;
      }
    }

    this.newRecipientDetailsForm.patchValue({
      firstName: respondent.firstName ?? '',
      lastName: respondent.lastName ?? '',
      email: respondent.email ?? '',
      mobileNumber: respondent.mobileNumber ?? null,
      dateOfBirth: formattedDateOfBirth,
      ethnicities: respondent.ethnicities ?? [],
      gender: respondent.gender ?? null,
      customGender: null,
      customEthnicity: null,
    });
  }

  /**
   * Start editing existing respondent
   */
  startEditingRespondent() {
    // Store original values for cancel
    this.originalRespondentValues = {
      firstName: this.newRecipientDetailsForm.controls.firstName.value,
      lastName: this.newRecipientDetailsForm.controls.lastName.value,
      email: this.newRecipientDetailsForm.controls.email.value,
      mobileNumber: this.newRecipientDetailsForm.controls.mobileNumber.value,
      dateOfBirth: this.newRecipientDetailsForm.controls.dateOfBirth.value,
      ethnicities:
        this.newRecipientDetailsForm.controls.ethnicities.value ?? [],
      gender: this.newRecipientDetailsForm.controls.gender.value,
      customGender: this.newRecipientDetailsForm.controls.customGender.value,
      customEthnicity:
        this.newRecipientDetailsForm.controls.customEthnicity.value,
    };
    this.isEditingRespondent.set(true);
  }

  /**
   * Cancel editing and restore original values
   */
  cancelEditingRespondent() {
    if (this.originalRespondentValues) {
      this.newRecipientDetailsForm.patchValue(this.originalRespondentValues);
    }
    this.isEditingRespondent.set(false);
    this.originalRespondentValues = null;
  }

  /**
   * Save edited respondent to the API
   */
  saveEditedRespondent() {
    if (!this.existingRespondentGuid) {
      return;
    }

    const currentEthnicities =
      this.newRecipientDetailsForm.controls.ethnicities.value ?? [];
    // Avoid mutating the original array from the form control — create a new array instead.
    const ethnicities =
      currentEthnicities.includes(Ethnicity.other) &&
      this.newRecipientDetailsForm.controls.customEthnicity.value
        ? [
            ...currentEthnicities,
            this.newRecipientDetailsForm.controls.customEthnicity.value,
          ]
        : [...currentEthnicities];

    this.surveyRespondentService
      .surveyRespondentsPut(this.authService.getAuthorisationHeaders(), {
        surveyRespondentGuid: this.existingRespondentGuid,
        body: {
          firstName: this.newRecipientDetailsForm.controls.firstName.value,
          lastName: this.newRecipientDetailsForm.controls.lastName.value,
          emailAddress: this.newRecipientDetailsForm.controls.email.value!,
          mobileNumber:
            this.newRecipientDetailsForm.controls.mobileNumber.value,
          dateOfBirth: this.newRecipientDetailsForm.controls.dateOfBirth.value,
          ethnicities: ethnicities,
          gender:
            this.newRecipientDetailsForm.controls.gender.value ===
            Gender.selfDescribe
              ? this.newRecipientDetailsForm.controls.customGender.value
              : this.newRecipientDetailsForm.controls.gender.value,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.success('Respondent Updated');
          this.isEditingRespondent.set(false);
          this.originalRespondentValues = null;
          // Refresh the respondents list to get updated data
          this.refreshExistingRespondents();
        },
        error: () => {
          this.toastService.error('Could Not Update Respondent');
        },
      });
  }

  /**
   * Refresh the existing respondents list from the API
   */
  refreshExistingRespondents() {
    this.surveyRespondentService
      .surveyRespondentsGet(this.authService.getAuthorisationHeaders(), {
        accountGuid: this.authService.getActiveAccountGuid()!,
        pageSize: 999999,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((respondents: PaginatedList<SurveyRespondentModel>) => {
        this.existingSurveyRespondentsPaginatedList = respondents;
        // Re-prefill the form with updated data
        this.prefillFormWithExistingRespondent();
      });
  }

  /**
   * Validation for new survey respondents when using a remote dispatch
   */
  validateNewSurveyRespondentDispatch() {
    if (this.surveyRespondentType !== SurveyRespondentType.New) {
      return true;
    }

    if (
      this.selectedDispatchType === InviteLinkDispatchType.Email &&
      !this.newRecipientDetailsForm.controls.email.value
    ) {
      return false;
    }

    return !(
      this.selectedDispatchType === InviteLinkDispatchType.TextMessage &&
      !this.newRecipientDetailsForm.controls.mobileNumber.value
    );
  }

  /**
   * Validation for existing survey respondents when using a remote dispatch
   */
  validateExistingSurveyRespondentDispatch() {
    if (this.surveyRespondentType !== SurveyRespondentType.Existing) {
      return true;
    }

    const selectedRespondent = this.getSelectedRespondent();
    if (!selectedRespondent) {
      return false;
    }

    if (
      this.selectedDispatchType === InviteLinkDispatchType.Email &&
      !selectedRespondent!.email
    ) {
      return false;
    }

    return !(
      this.selectedDispatchType === InviteLinkDispatchType.TextMessage &&
      !selectedRespondent.mobileNumber
    );
  }

  // eslint-disable-next-line
  protected unsorted(a: KeyValue<any, any>, b: KeyValue<any, any>): number {
    return 0;
  }

  protected readonly setTimeout = setTimeout;

  getSelectedQuestionnaireName(): string {
    const q = this.availableQuestionnaires.find(
      (q) => q.guid === this.selectedQuestionnaireGuid,
    );
    return q?.name ?? 'Select...';
  }

  getRespondentTypeLabel(): string {
    switch (this.surveyRespondentType) {
      case SurveyRespondentType.Anonymous:
        return 'Anonymous';
      case SurveyRespondentType.Existing:
        return 'Existing';
      case SurveyRespondentType.New:
        return 'New';
      default:
        return 'Select...';
    }
  }

  getCompletionLabel(): string {
    if (!this.selectedDispatchType) return 'Do on this device';
    // Convert enum value to readable label
    const entry = Object.entries(InviteLinkDispatchType).find(
      ([, v]) => v === this.selectedDispatchType,
    );
    return entry ? entry[0].replace(/([A-Z])/g, ' $1').trim() : 'Select...';
  }

  getGenderLabel(): string {
    const val = this.newRecipientDetailsForm.controls.gender.value;
    return val ?? 'Select...';
  }

  getEthnicityLabel(): string {
    const ethnicities =
      this.newRecipientDetailsForm.controls.ethnicities.value ?? [];
    if (ethnicities.length === 0) return 'Select...';
    if (ethnicities.length === 1) return ethnicities[0];
    return `${ethnicities[0]} +${ethnicities.length - 1} more`;
  }

  toggleEthnicity(value: string) {
    const current =
      this.newRecipientDetailsForm.controls.ethnicities.value ?? [];
    const index = current.indexOf(value);
    if (index === -1) {
      this.newRecipientDetailsForm.controls.ethnicities.setValue([
        ...current,
        value,
      ]);
    } else {
      this.newRecipientDetailsForm.controls.ethnicities.setValue(
        current.filter((e) => e !== value),
      );
    }
  }

  isEthnicitySelected(value: string): boolean {
    const ethnicities =
      this.newRecipientDetailsForm.controls.ethnicities.value ?? [];
    return ethnicities.includes(value);
  }

  /**
   * Format a date string for display, returns null if invalid or default date (year 0001)
   */
  formatDisplayDate(dateString: string | undefined | null): string | null {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return null;
    return dateString;
  }

  getCreatedAtDisplay(): string {
    const respondent = this.getSelectedRespondent();
    const formatted = this.formatDisplayDate(respondent?.createdAt);
    return formatted
      ? new Date(formatted).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '-';
  }

  getModifiedAtDisplay(): string {
    const respondent = this.getSelectedRespondent();
    const formatted = this.formatDisplayDate(respondent?.modifiedAt);
    return formatted
      ? new Date(formatted).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '-';
  }
}
